import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, HandCoins, Prohibit, CheckCircle, XCircle, Trash } from "@phosphor-icons/react";

const peso = (v) => `₱${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_BADGE = {
  pending: "bg-warning/10 text-warning border-warning/20",
  approved: "bg-success/10 text-success border-success/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
};

export default function Loans() {
  const { isAdmin } = useAuth();
  return isAdmin ? <LoansAdmin /> : <LoansEmployee />;
}

function Header({ subtitle }) {
  return (
    <div>
      <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground">Financing</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Loans</h1>
      <p className="text-muted-foreground mt-2">{subtitle}</p>
    </div>
  );
}

function LoansEmployee() {
  const [requests, setRequests] = useState([]);
  const [balance, setBalance] = useState(0);
  const [form, setForm] = useState({ amount: "", purpose: "", monthly_deduction: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: reqs }, { data: stats }] = await Promise.all([
        api.get("/loans/requests"),
        api.get("/stats"),
      ]);
      setRequests(reqs);
      setBalance(stats?.my_loan_balance ?? 0);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post("/loans/requests", {
        amount: Number(form.amount),
        purpose: form.purpose,
        monthly_deduction: form.monthly_deduction ? Number(form.monthly_deduction) : null,
      });
      toast.success("Loan request submitted for approval");
      setForm({ amount: "", purpose: "", monthly_deduction: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="loans-page">
      <Header subtitle="Request a company loan and track your balance." />

      <Card className="p-5 border-border shadow-none max-w-sm">
        <Wallet weight="duotone" className="h-6 w-6 text-primary" />
        <p className="mt-3 text-2xl font-heading font-extrabold" data-testid="loan-balance">{peso(balance)}</p>
        <p className="text-sm text-muted-foreground">Current loan balance</p>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 border-border shadow-none lg:col-span-1 h-fit">
          <div className="flex items-center gap-2 mb-4">
            <HandCoins weight="duotone" className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-lg font-bold">Request a loan</h2>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="loan-form">
            <div className="space-y-1.5">
              <Label>Amount (₱)</Label>
              <Input data-testid="loan-amount" type="number" min="1" step="0.01" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="e.g. 10000" required />
            </div>
            <div className="space-y-1.5">
              <Label>Preferred monthly deduction (₱)</Label>
              <Input data-testid="loan-monthly" type="number" min="0" step="0.01" value={form.monthly_deduction}
                onChange={(e) => setForm({ ...form, monthly_deduction: e.target.value })} placeholder="e.g. 1000" />
            </div>
            <div className="space-y-1.5">
              <Label>Purpose</Label>
              <Textarea data-testid="loan-purpose" placeholder="Brief purpose of the loan..." value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })} required />
            </div>
            <Button type="submit" data-testid="submit-loan-button" disabled={busy} className="w-full font-semibold">
              {busy ? "Submitting..." : "Submit request"}
            </Button>
          </form>
        </Card>

        <Card className="border-border shadow-none lg:col-span-2 overflow-hidden">
          <div className="p-4 border-b border-border"><h2 className="font-heading font-bold">My requests</h2></div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead>Amount</TableHead>
                  <TableHead>Monthly</TableHead>
                  <TableHead>Purpose</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!requests.length ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">
                    <Prohibit className="h-6 w-6 mx-auto mb-2 opacity-40" />No loan requests yet.
                  </TableCell></TableRow>
                ) : requests.map((r) => (
                  <TableRow key={r.id} data-testid={`loan-request-${r.id}`} className="animate-fade-up">
                    <TableCell className="font-semibold">{peso(r.amount)}</TableCell>
                    <TableCell className="text-muted-foreground">{r.monthly_deduction ? peso(r.monthly_deduction) : "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[220px] truncate">{r.purpose || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`capitalize ${STATUS_BADGE[r.status]}`} data-testid={`loan-status-${r.id}`}>{r.status}</Badge>
                      {r.decided_at && (
                        <p className="text-[11px] text-muted-foreground mt-1" data-testid={`loan-decided-${r.id}`}>
                          {r.status === "approved" ? "Approved" : "Rejected"} on {new Date(r.decided_at).toLocaleDateString()}
                        </p>
                      )}
                      {r.status === "rejected" && r.decision_note && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">Note: {r.decision_note}</p>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
}

function LoansAdmin() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/loans/requests");
      setRequests(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, status, noteVal = "") => {
    try {
      await api.put(`/loans/requests/${id}/decision`, { status, note: noteVal });
      toast.success(status === "approved" ? "Loan approved & added to balance" : "Loan rejected");
      setRejectTarget(null); setNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const pendingCount = requests.filter((r) => r.status === "pending").length;

  const remove = async (id) => {
    try {
      const { data } = await api.delete(`/loans/requests/${id}`);
      toast.success(data.reversed ? `Loan deleted · ${peso(data.reversed)} reversed from balance` : "Loan request deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="loans-page">
      <Header subtitle={`Review and approve employee loan requests. ${pendingCount} pending.`} />

      <Card className="border-border shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead>Employee</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Monthly</TableHead>
                <TableHead>Purpose</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : !requests.length ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No loan requests yet.</TableCell></TableRow>
              ) : requests.map((r) => (
                <TableRow key={r.id} data-testid={`loan-request-${r.id}`} className="animate-fade-up">
                  <TableCell className="font-medium">{r.employee_name}</TableCell>
                  <TableCell className="font-semibold">{peso(r.amount)}</TableCell>
                  <TableCell className="text-muted-foreground">{r.monthly_deduction ? peso(r.monthly_deduction) : "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-sm max-w-[240px] truncate">{r.purpose || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`capitalize ${STATUS_BADGE[r.status]}`} data-testid={`loan-status-${r.id}`}>{r.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {r.status === "pending" ? (
                        <>
                          <Button size="sm" className="gap-1.5 bg-success hover:bg-success/90" data-testid={`approve-loan-${r.id}`}
                            onClick={() => decide(r.id, "approved")}>
                            <CheckCircle weight="bold" className="h-4 w-4" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="gap-1.5 text-destructive" data-testid={`reject-loan-${r.id}`}
                            onClick={() => { setRejectTarget(r); setNote(""); }}>
                            <XCircle weight="bold" className="h-4 w-4" /> Reject
                          </Button>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground mr-1" data-testid={`loan-decided-${r.id}`}>
                          {r.decided_at ? `${new Date(r.decided_at).toLocaleDateString()}${r.decided_by_name ? ` · ${r.decided_by_name}` : ""}` : ""}
                        </span>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`delete-loan-${r.id}`}
                        onClick={() => setDeleteTarget(r)}>
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="reject-loan-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reject loan request</DialogTitle>
            <DialogDescription>
              {rejectTarget ? `${rejectTarget.employee_name} · ${peso(rejectTarget.amount)}` : ""} — add an optional note the employee will see.
            </DialogDescription>
          </DialogHeader>
          <Textarea data-testid="reject-note" placeholder="Reason for rejection (optional)..." value={note}
            onChange={(e) => setNote(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button variant="destructive" data-testid="confirm-reject-loan"
              onClick={() => decide(rejectTarget.id, "rejected", note)}>Reject request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="delete-loan-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete loan request</DialogTitle>
            <DialogDescription>
              {deleteTarget ? `${deleteTarget.employee_name} · ${peso(deleteTarget.amount)}.` : ""}
              {deleteTarget?.status === "approved" ? " Since this was approved, the amount will be reversed from their loan balance." : ""}
              {" This permanently removes the request and cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" data-testid="confirm-delete-loan"
              onClick={() => remove(deleteTarget.id)}>Delete request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
